/* eslint-disable react-hooks/rules-of-hooks */

import {
  Form,
  FormField,
  FormControl,
  FormItem,
  Textarea,
  FormLabel,
  Button,
  Checkbox,
} from "@repo/ui";
import { StatusContext } from "pages/tickets/constants";
import { getParentTicketList } from "pages/tickets/utils";
import { useContext, useEffect, useMemo } from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import {
  useLookupResolutionCodes,
  useLookupResolutionCodesViaCategory,
} from "services/lookups";
import { useUpdateMaintenanceStatus } from "services/maintenance";
import { useUpdateTicketStatus } from "services/tickets";
import { FormDropdown } from "ui/combo-box";
import FormInput from "ui/Input";
import { Label } from "ui/label";
import Loader from "ui/page-loader";

/**
 * @typedef {Object} Ticket
 * @property {boolean} isServiceTicket - Indicates if the ticket is a service ticket
 */

/**
 * ResolutionNotes component for handling ticket resolution details
 * @param {Object} props - Component props
 * @param {string} props.requestId - The ID of the ticket/request
 * @param {boolean} props.ticketView - Flag indicating if in ticket view mode
 * @param {Ticket} [props.ticket] - The ticket object
 */
function ResolutionNotes({ requestId, ticketView, ticket = null }) {
  const { status } = useContext(StatusContext);
  const navigate = useNavigate();

  // Hook selection based on ticketView
  const resolutionCodesHook = ticketView
    ? useLookupResolutionCodesViaCategory()
    : useLookupResolutionCodes();

  const statusUpdateHook = ticketView
    ? useUpdateTicketStatus()
    : useUpdateMaintenanceStatus();

  const {
    mutate: getResolutionCodes,
    data: resolutionCodesList,
    isPending: gettingResolutionCode,
  } = resolutionCodesHook;

  const {
    mutate: updateStatus,
    data: updateStatusRes,
    isPending: updatingStatus,
  } = statusUpdateHook;

  // Fetch resolution codes on mount
  useEffect(() => {
    if (ticketView) {
      if (ticket && !ticket.isServiceTicket) {
        //if not service ticket
        getResolutionCodes("CC Tickets");
      }
    } else {
      getResolutionCodes();
    }
  }, [ticketView, ticket, getResolutionCodes]);

  // Memoize home URL
  const getHomeURL = useMemo(() => {
    return getParentTicketList(
      status,
      ticket ? !ticket.isServiceTicket : false
    );
  }, [ticket, status]);

  // Navigate after successful status update
  useEffect(() => {
    if (updateStatusRes) {
      navigate(getHomeURL);
    }
  }, [updateStatusRes, navigate, getHomeURL]);

  // Form setup
  const form = useForm({
    defaultValues: {
      resolvedBy: "",
      resolved: "",
      resolutionCode: "",
      ITName: "",
      resolutionNotes: "",
      cause: "",
      addToComments: true,
    },
  });

  const onSubmit = (data) => {
    const basePayload = {
      resolutionCode: data.resolutionCode,
      resolutionNotes: data.resolutionNotes,
      itName: data.ITName,
      addToComments: data.addToComments,
      cause: data.cause,
    };

    const payload =
      ticketView && ticket
        ? {
            ...basePayload,
            ticketId: requestId,
            status: "Resolved",
          }
        : {
            ...basePayload,
            requestId,
          };

    updateStatus(payload);
  };

  const generalProps = {
    errors: form.formState.errors,
    control: form.control,
  };

  return (
    <Form {...form} className="space-y-8 custom-form-v2">
      {gettingResolutionCode || updatingStatus ? <Loader /> : null}

      <form>
        <div className="form-row-d-50">
          <FormInput
            {...generalProps}
            label="Resolved By"
            name="resolvedBy"
            disabled
          />
          <FormInput
            {...generalProps}
            label="Resolved"
            name="resolved"
            disabled
          />

          <FormDropdown
            {...generalProps}
            isRequired
            label="Resolution Code"
            name="resolutionCode"
            data={resolutionCodesList?.data || []}
            id="description"
            text="description"
            placeholder="Select Resolution Code"
            rules={{ required: "Select Value" }}
          />

          {!ticketView && (
            <FormInput {...generalProps} label="IT Name" name="ITName" />
          )}

          <FormField
            control={form.control}
            name="addToComments"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="w-full">
                  Add Resolution Notes To Comments
                </FormLabel>
                <FormControl>
                  <Checkbox
                    id="addToComments"
                    checked={field.value}
                    onCheckedChange={field.onChange}
                  />
                </FormControl>
              </FormItem>
            )}
          />
        </div>

        <FormTextAreaField
          isRequired
          control={form.control}
          name="resolutionNotes"
          label="Resolution Notes"
          errors={form.formState.errors}
          rules={{ required: "Select Value" }}
        />

        <FormTextAreaField
          control={form.control}
          name="cause"
          label="Cause"
          errors={form.formState.errors}
        />

        <div className="flex flex-row w-full justify-end mt-6">
          <Button variant="secondary" onClick={form.handleSubmit(onSubmit)}>
            Update Resolution Notes
          </Button>
        </div>
      </form>
    </Form>
  );
}

// Helper component for text area fields
function FormTextAreaField({
  control,
  name,
  label,
  errors,
  rules,
  isRequired,
}) {
  return (
    <div className="flex flex-col gap-inherit w-full mt-6">
      <FormField
        control={control}
        name={name}
        rules={rules}
        render={({ field }) => (
          <FormItem>
            <div className="flex flex-col gap-6 w-full">
              <Label label={label} isRequired={isRequired} />

              <div>
                <FormControl>
                  <Textarea
                    {...field}
                    className="min-w-full"
                    style={errors?.[name] ? { borderColor: "red" } : {}}
                  />
                </FormControl>
                {errors?.[name] ? (
                  <p className="text-destructive text-xs">
                    {errors[name].message}
                  </p>
                ) : null}
              </div>
            </div>
          </FormItem>
        )}
      />
    </div>
  );
}

ResolutionNotes.defaultProps = {
  ticket: null,
};

export default ResolutionNotes;
